import React, {useState} from 'react';
import {Ticket, TicketData} from "types/Ticket";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import MoneyInput from "components/Form/MoneyInput";
import PaymentMethod from "types/PaymentMethod";
import {formatDateTimeForInput} from "services/date";
import {DateTime} from "luxon";
import {useQuery} from "react-query";
import ngage from 'services/ngage';
import Selector from "components/Form/Selector";
import {Item} from "components/Form/Selector/Selector";
import {User} from 'types/User';
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import Select from "components/Form/Select";
import {Option} from "components/Form/Select/Select";
import IconName from "types/IconName";
import useMoney, {DiscountMode} from "../../../hooks/useMoney";
import Alert from "../../../components/Alert";
import {toTitleCase} from "../../../services/string";
import TicketingSource from "../../../types/TicketingSource";
import {TicketType} from "../../../types/TicketType";
import {MembershipData} from "../../../types/Membership";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import Toggle from "../../../components/Form/Toggle";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {useOperator} from "../../../context/OperatorContext";

// Constants ----

const paymentMethods: Array<Item> = [
    {id: PaymentMethod.NONE, name: 'None'},
    {id: PaymentMethod.CARD, name: 'Card'},
    {id: PaymentMethod.CASH, name: 'Cash'}
]

// Helpers ----

function userOption(user: User): Option {
    return {
        id: user.id,
        name: user.name,
        description: user.email,
        icon: IconName.USER
    }
}

function calculateDiscount({price, discountAmount, discountPercent, discountMode}: CalculateDiscountOptions) {
    switch (discountMode) {
        case DiscountMode.PERCENT:
            return (price / 100) * discountPercent;
        case DiscountMode.AMOUNT:
            return discountAmount;
    }
}

// Types ----

type Props = {
    ticket?: Ticket
    onSubmit: (values: TicketData, helpers: FormikHelpers<TicketData>) => any
}

type CalculateDiscountOptions = {
    price: number
    discountAmount: number
    discountPercent: number
    discountMode: DiscountMode
}

// Component ----

function TicketForm(props: Props) {
    const {termFor} = useOperator();
    const {formattedMoney} = useMoney();

    const isDayTicket = (props.ticket?.valid_at && props.ticket?.expires_at)
        ? props.ticket.valid_at.hasSame(props.ticket?.expires_at, 'day')
        : true;

    const [selectedType, setSelectedType] = useState<TicketType | null>(props.ticket?.type || null);
    const [discountMode, setDiscountMode] = useState<DiscountMode>(DiscountMode.PERCENT);
    const [dayTicket, setDayTicket] = useState<boolean>(isDayTicket);

    const {data: types, isLoading: typesLoading} = useQuery(
        ['ticket-types', {page: 1}],
        () => ngage.ticketTypes.all(1)
    )

    function handleSelectType(type: Item, formik: FormikProps<MembershipData>) {
        if (types) {
            void formik.setFieldValue('type_id', type.id)
            setSelectedType(types.data.find(p => p.id === Number(type.id)) || null)
        }
    }

    const userSource = async (query: string) => {
        return (await ngage.users.filtered({search: query || null}))?.data.map(userOption) || []
    }

    const handleSelectHolder = (holder: Option, formik: FormikProps<TicketData>) => {
        void formik.setFieldValue('holder_id', holder?.id ?? 0)
    }

    function selectDiscountMode(index: number, {setFieldValue}: FormikHelpers<any>) {
        switch (index) {
            case DiscountMode.PERCENT:
                void setFieldValue('discount_percent', props.ticket?.discount.percent);
                void setFieldValue('discount_amount', undefined);
                break;

            case DiscountMode.AMOUNT:
                void setFieldValue('discount_amount', props.ticket?.discount.amount);
                void setFieldValue('discount_percent', undefined);
                break;
        }

        setDiscountMode(index);
    }

    function handleDayTicketToggle(checked: boolean, formik: FormikProps<TicketData>) {
        const validAt = DateTime.fromISO(formik.values.valid_at ?? '').endOf('day').toISO() ?? '';

        // If the toggle is enabled set the 'expires_at' value to be based on the 'valid_at' date
        if (checked) {
            void formik.setFieldValue('expires_at', validAt);
        }

        setDayTicket(checked);
    }

    const isExternal = props.ticket && props.ticket?.source.name !== TicketingSource.NGAGE;
    const isFree = !selectedType?.price;

    const validationSchema = Yup.object().shape({
        type_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
        valid_at: Yup.date().required("This field is required"),
        expires_at:
          Yup.date().when(['valid_at'], ([validAt]: any, schema: any) => {
              if (!validAt) return schema.required('This field is required');

              return schema.min(validAt, "Must not be before 'Valid at' date").required('This field is required');
          }),
        holder_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    })

    const initialValues: TicketData = {
        type_id: props.ticket?.type.id || 0,
        discount_percent: props.ticket?.discount.percent || 0,
        discount_amount: undefined,
        paid_amount: props.ticket?.payment.amount || 0,
        payment_method: props.ticket?.payment.method || PaymentMethod.NONE,
        valid_at: (props.ticket?.valid_at || DateTime.local().startOf('day')).toISO(),
        expires_at: (props.ticket?.expires_at || DateTime.local().endOf('day')).toISO(),
        holder_id: props.ticket?.holder.id || 0,
    }

    return (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={props.onSubmit}
        >
            {formik => {
                const discountValue = calculateDiscount({
                    price: selectedType?.price ?? 0,
                    discountMode,
                    discountAmount: formik.values.discount_amount ?? 0,
                    discountPercent: formik.values.discount_percent ?? 0,
                });

                const ticketPrice = formattedMoney(selectedType?.price ?? 0, props.ticket?.currency);
                const discountAmount = formattedMoney(discountValue ?? 0, props.ticket?.currency);
                const paidValue = formik.values.paid_amount ?? 0;
                const amountDue = formattedMoney(((selectedType?.price ?? 0) - discountValue - paidValue) ?? 0, props.ticket?.currency);

                return (
                    <Form className="space-y-8 divide-y divide-gray-200">
                        <ErrorNotification formik={formik} />

                        <div className="shadow sm:rounded-md">
                            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">

                                {isExternal &&
                                  <Alert
                                    message={`This ticket has been imported from ${toTitleCase(props.ticket?.source.name ?? '')}, so some details cannot be changed here.`}/>
                                }

                                <div className="space-y-8 divide-y divide-gray-200">
                                    <div>
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Key Information
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                Basic details about the {termFor('ticket')}
                                            </p>
                                        </div>
                                        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                            {/* Type */}
                                            <div className="sm:col-span-3">
                                                <Selector label="Type *"
                                                          isLoading={typesLoading}
                                                          items={types?.data.map(type => ({id: type.id, name: type.name, description: `Single-use ticket for ${formattedMoney(type.price, type.currency)}`, icon: 'color-swatch'})) || []}
                                                          selectedId={formik.values.type_id}
                                                          onChange={item => handleSelectType(item, formik)}
                                                          disabled={!!props.ticket}
                                                />

                                                {(!!formik.errors?.type_id && !!formik.touched?.type_id) &&
                                                  <FieldHelp isError>
                                                      {formik.errors.type_id}
                                                  </FieldHelp>
                                                }
                                            </div>

                                            {/* Day ticket toggle */}
                                            <div className="sm:col-start-1 sm:col-span-3">
                                                <Toggle
                                                  label='Single day'
                                                  checked={dayTicket}
                                                  onChange={(checked) => handleDayTicketToggle(checked, formik)}
                                                />
                                            </div>

                                            {/* Valid At */}
                                            <div className="sm:col-span-3 sm:col-start-1 md:col-span-2 md:col-start-1">
                                                <FieldHeader label="Valid At *" fieldId='valid_at' />
                                                <div className="mt-1">
                                                    <Field
                                                        type="date"
                                                        name="valid_at"
                                                        id="valid_at"
                                                        value={formatDateTimeForInput(formik.values.valid_at, true)}
                                                        onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                                            void formik.setFieldValue('valid_at', DateTime.fromISO(event.currentTarget.value).startOf('day').toISO())
                                                        }}
                                                        disabled={isExternal}
                                                    />
                                                </div>
                                            </div>

                                            {/* Expires At */}
                                            {!dayTicket &&
                                                <div className="sm:col-span-3 md:col-span-2">
                                                    <FieldHeader label="Expires At *" fieldId='expires_at' />
                                                    <div className="mt-1">
                                                        <Field
                                                            type="date"
                                                            name="expires_at"
                                                            id="expires_at"
                                                            value={formatDateTimeForInput(formik.values.expires_at, true)}
                                                            onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                                                void formik.setFieldValue('expires_at', DateTime.fromISO(event.currentTarget.value).endOf('day').toISO())
                                                            }}
                                                            disabled={isExternal}
                                                            min={formik.values.valid_at ? DateTime.fromISO(formik.values.valid_at ?? '').toISODate() : null}
                                                        />
                                                    </div>
                                                </div>
                                            }

                                            {/* Holder */}
                                            <div className="sm:col-span-3 sm:col-start-1">
                                                <Select
                                                    label="Holder *"
                                                    hint="Type to search"
                                                    source={userSource}
                                                    selected={props.ticket?.holder}
                                                    onChange={selected => handleSelectHolder(selected, formik)}
                                                    disabled={isExternal}
                                                />

                                                {(!!formik.errors?.holder_id && !!formik.touched?.holder_id) &&
                                                  <FieldHelp isError>
                                                      {formik.errors.holder_id}
                                                  </FieldHelp>
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {!isFree &&
                                        <div className="pt-8">
                                            <div>
                                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                    Discount
                                                </h3>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    Apply discount on this ticket
                                                </p>
                                            </div>
                                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                                {/* Discount Percent/Amount */}
                                                <div className="sm:col-span-2">
                                                    <FieldHeader label="Discount" />
                                                    <Tab.Group selectedIndex={discountMode} onChange={index => selectDiscountMode(index, formik)}>
                                                        <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                                                            <TabButton position={TabButton.positions.FIRST} disabled={isExternal}>Percent</TabButton>
                                                            <TabButton position={TabButton.positions.LAST} disabled={isExternal}>Amount</TabButton>
                                                        </Tab.List>
                                                        <Tab.Panels>
                                                            <Tab.Panel>
                                                                <Field
                                                                    type="number"
                                                                    name="discount_percent"
                                                                    id="discount_percent"
                                                                    addon="%"
                                                                    min={0}
                                                                    max={100}
                                                                    step={0.01}
                                                                    disabled={isExternal}
                                                                />
                                                            </Tab.Panel>
                                                            <Tab.Panel>
                                                                <MoneyInput
                                                                    currency={MoneyInput.Currency.GBP}
                                                                    value={formik.values?.discount_amount}
                                                                    onChange={amount => formik.setFieldValue('discount_amount', amount || 0)}
                                                                    disabled={isExternal}
                                                                />
                                                            </Tab.Panel>
                                                        </Tab.Panels>
                                                    </Tab.Group>
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {!isFree &&
                                        <div className="pt-8">
                                            <div>
                                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                    Payment
                                                </h3>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    Record payment towards this ticket
                                                </p>

                                                <p className="text-sm text-gray-700 mt-6 font-medium">
                                                    Ticket price: {ticketPrice}
                                                </p>

                                                <p className="text-sm text-gray-700 mt-2 font-medium">
                                                    Discount: {discountAmount}
                                                </p>

                                                <p className="text-sm text-gray-700 mt-2 font-medium">
                                                    Amount Due: {amountDue}
                                                </p>
                                            </div>
                                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                                {/* Paid */}
                                                <div className="sm:col-span-2">
                                                    <FieldHeader label="Amount Paid"/>
                                                    <div className="mt-1">
                                                    <MoneyInput
                                                            currency={MoneyInput.Currency.GBP}
                                                            value={formik.values.paid_amount}
                                                            onChange={amount => formik.setFieldValue('paid_amount', amount || 0)}
                                                            disabled={isExternal}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Payment Method */}
                                                <div className="sm:col-span-2">
                                                    <Selector label="Payment Method"
                                                              items={paymentMethods}
                                                              selectedId={formik.values.payment_method}
                                                              onChange={item => formik.setFieldValue('payment_method', item.id)}
                                                              disabled={isExternal}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>

                            <FormControls formik={formik} />
                        </div>
                    </Form>
                )
            }}
        </Formik>
    );
}

export default TicketForm;